/**
 * Utility functions for working with cron expressions and scheduling
 */
export class CronUtils {
  /**
   * Validate a cron expression
   * Basic validation for standard 5-field cron expressions
   */
  public static validateCronExpression(expression: string): boolean {
    if (!expression || typeof expression !== 'string') {
      return false;
    }

    const parts = expression.trim().split(/\s+/);

    // Should have 5 parts: minute, hour, day, month, day-of-week
    if (parts.length !== 5) {
      return false;
    }

    const [minute, hour, day, month, dayOfWeek] = parts;

    // Basic validation patterns
    const patterns = {
      minute: /^(\*|([0-5]?\d)(,([0-5]?\d))*|([0-5]?\d)-([0-5]?\d)|(\*\/([1-9]\d*)))$/,
      hour: /^(\*|(1?\d|2[0-3])(,(1?\d|2[0-3]))*|(1?\d|2[0-3])-(1?\d|2[0-3])|(\*\/([1-9]\d*)))$/,
      day: /^(\*|([1-9]|[12]\d|3[01])(,([1-9]|[12]\d|3[01]))*|([1-9]|[12]\d|3[01])-([1-9]|[12]\d|3[01])|(\*\/([1-9]\d*)))$/,
      month: /^(\*|([1-9]|1[0-2])(,([1-9]|1[0-2]))*|([1-9]|1[0-2])-([1-9]|1[0-2])|(\*\/([1-9]\d*)))$/,
      dayOfWeek: /^(\*|[0-6](,[0-6])*|[0-6]-[0-6]|(\*\/([1-7])))$/,
    };

    return (
      patterns.minute.test(minute) &&
      patterns.hour.test(hour) &&
      patterns.day.test(day) &&
      patterns.month.test(month) &&
      patterns.dayOfWeek.test(dayOfWeek)
    );
  }

  /**
   * Common cron expressions
   */
  public static readonly commonExpressions = {
    everyMinute: '* * * * *',
    every5Minutes: '*/5 * * * *',
    every15Minutes: '*/15 * * * *',
    every30Minutes: '*/30 * * * *',
    everyHour: '0 * * * *',
    everyDay: '0 0 * * *',
    everyWeek: '0 0 * * 0',
    everyMonth: '0 0 1 * *',
    everyYear: '0 0 1 1 *',
    weekdays: '0 9 * * 1-5',
    weekends: '0 10 * * 0,6',
  };

  /**
   * Parse cron expression into human-readable format
   */
  public static parseCronExpression(expression: string): string {
    if (!this.validateCronExpression(expression)) {
      return 'Invalid cron expression';
    }

    // Check against common expressions first
    for (const [key, value] of Object.entries(this.commonExpressions)) {
      if (value === expression) {
        return this.humanizeCommonExpression(key);
      }
    }

    const [minute, hour, day, month, dayOfWeek] = expression.split(/\s+/);

    let result = 'At ';

    // Handle minute and hour
    if (minute === '*' && hour === '*') {
      result += 'every minute';
    } else if (minute === '0' && hour === '*') {
      result += 'every hour';
    } else if (minute === '0' && hour !== '*') {
      result += `${hour}:00`;
    } else if (minute !== '*' && hour !== '*') {
      result += `${hour}:${minute.padStart(2, '0')}`;
    } else {
      result += `minute ${minute} of every hour`;
    }

    // Handle day, month, day of week
    if (day !== '*' || month !== '*' || dayOfWeek !== '*') {
      result += ' on ';

      if (dayOfWeek !== '*') {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        if (dayOfWeek.includes(',')) {
          const dayNumbers = dayOfWeek.split(',').map((d) => parseInt(d));
          result += dayNumbers.map((d) => days[d]).join(', ');
        } else if (dayOfWeek.includes('-')) {
          const [start, end] = dayOfWeek.split('-').map((d) => parseInt(d));
          result += `${days[start]} through ${days[end]}`;
        } else {
          result += days[parseInt(dayOfWeek)];
        }
      }

      if (day !== '*') {
        result += ` the ${day}${this.getOrdinalSuffix(parseInt(day))}`;
      }

      if (month !== '*') {
        const months = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        result += ` of ${months[parseInt(month) - 1]}`;
      }
    }

    return result;
  }

  private static humanizeCommonExpression(key: string): string {
    const map: Record<string, string> = {
      everyMinute: 'Every minute',
      every5Minutes: 'Every 5 minutes',
      every15Minutes: 'Every 15 minutes',
      every30Minutes: 'Every 30 minutes',
      everyHour: 'Every hour',
      everyDay: 'Every day at midnight',
      everyWeek: 'Every week on Sunday at midnight',
      everyMonth: 'Every month on the 1st at midnight',
      everyYear: 'Every year on January 1st at midnight',
      weekdays: 'Every weekday at 9:00 AM',
      weekends: 'Every weekend at 10:00 AM',
    };
    return map[key] || key;
  }

  private static getOrdinalSuffix(num: number): string {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  /**
   * Get next execution times for a cron expression
   * Note: This is a simplified implementation
   */
  public static getNextExecutions(expression: string, count: number = 5): Date[] {
    if (!this.validateCronExpression(expression)) {
      return [];
    }

    const executions: Date[] = [];
    const now = new Date();
    let current = new Date(now.getTime() + 60000); // Start from next minute

    // Simplified calculation - in production, use a proper cron parser library
    const [minute, hour, day, month, dayOfWeek] = expression.split(/\s+/);

    for (let i = 0; i < count && executions.length < count; i++) {
      if (this.matchesCronExpression(current, minute, hour, day, month, dayOfWeek)) {
        executions.push(new Date(current));
      }
      current = new Date(current.getTime() + 60000); // Add 1 minute
    }

    return executions;
  }

  private static matchesCronExpression(
    date: Date,
    minute: string,
    hour: string,
    day: string,
    month: string,
    dayOfWeek: string,
  ): boolean {
    // Simplified matching - in production, use a proper cron parser
    const matches = {
      minute: minute === '*' || parseInt(minute) === date.getMinutes(),
      hour: hour === '*' || parseInt(hour) === date.getHours(),
      day: day === '*' || parseInt(day) === date.getDate(),
      month: month === '*' || parseInt(month) === date.getMonth() + 1,
      dayOfWeek: dayOfWeek === '*' || parseInt(dayOfWeek) === date.getDay(),
    };

    return Object.values(matches).every(Boolean);
  }

  /**
   * Create a cron expression from components
   */
  public static createCronExpression(options: {
    minute?: number | string;
    hour?: number | string;
    day?: number | string;
    month?: number | string;
    dayOfWeek?: number | string;
  }): string {
    const { minute = '*', hour = '*', day = '*', month = '*', dayOfWeek = '*' } = options;

    return `${minute} ${hour} ${day} ${month} ${dayOfWeek}`;
  }
}
